import {useEffect} from "react";
import {animate, easeOut, useMotionValue, useReducedMotion, type MotionValue} from "framer-motion";

/**
 * A number that TRAVELS to each new target instead of snapping to it (ui#942).
 *
 * The motion value is deliberately not React state: it is written by the animation loop, so a card
 * showing four of these re-renders zero times while they move — the DOM text is updated by
 * framer-motion's own subscription.
 *
 * `prefers-reduced-motion` is honoured by SETTING the value rather than by shortening the travel:
 * `MotionConfig reducedMotion="user"` (AppProviders) only governs transform and layout animations,
 * so an explicitly animated value like this one has to answer the preference itself.
 */

/** Travel time for a value that lands on a new target about once a second (speed, ETA). */
export const EASED_VALUE_SECONDS = 0.7;

/** Travel time matched to the meters' own `width` transition, for values that track a bar. */
export const EASED_METER_SECONDS = 0.35;

/**
 * The curve every travel below follows, as a FUNCTION rather than the `"easeOut"` name.
 *
 * Identical to the name at runtime — framer-motion's easing map resolves `"easeOut"` to this very
 * export, and passes a function definition through untouched — but a name is a string that nothing
 * can import. `e2e/progress-gauge.spec.ts` bounds how far the painted number can have travelled by
 * evaluating this curve, and while the app spelled its ease as a literal that bound was an
 * independent copy: changing the app to a curve that rises more slowly early would have left the
 * bound too generous and the journey green (ui#1220). Exported so the assertion and the animation
 * cannot disagree about what "eased" means.
 */
export const EASED_VALUE_EASE = easeOut;

export function useEasedValue(target: number, durationSeconds = EASED_VALUE_SECONDS): MotionValue<number> {
	const reducedMotion = useReducedMotion() ?? false;
	const value = useMotionValue(target);

	useEffect(() => {
		if (reducedMotion) {
			value.set(target);
			return;
		}
		// From wherever the previous travel got to, never from the previous target: a new reading can
		// land while the last one is still moving, and restarting from the target would jump.
		const travel = animate(value, target, {duration: durationSeconds, ease: EASED_VALUE_EASE});
		return () => travel.stop();
	}, [durationSeconds, reducedMotion, target, value]);

	return value;
}
